import { useMemo, useState } from "react";
import { createGame } from "@kikik/mahjong-core";
import { describeSituation } from "@kikik/mahjong-coach";
import {
  CoachPanel,
  EndPanel,
  EventLog,
  GameShell,
  GameTable,
  HelpPanel,
  TableSettings,
} from "../components";
import type { ShellNav } from "../components";
import { useI18n, usePlayerNames } from "../i18n";
import { computeWaits } from "../game/tableModel";
import { useLocalGame } from "../game/useLocalGame";
import type { SeatController } from "../game/useLocalGame";

export interface SoloModeProps {
  seed?: string;
  botDelayMs?: number;
  controllers?: readonly SeatController[];
  /** Supplied by the app shell; absent when a test renders the mode directly. */
  nav?: ShellNav;
}

function newSeed(): string {
  return `kikik-${Date.now().toString(36)}`;
}

export function SoloMode({ seed, botDelayMs, controllers, nav }: SoloModeProps) {
  const { t, tm } = useI18n();
  const names = usePlayerNames();
  const [activeSeed, setActiveSeed] = useState(() => seed ?? newSeed());
  const [seedInput, setSeedInput] = useState(activeSeed);
  const [gameSerial, setGameSerial] = useState(0);

  const game = useLocalGame({
    createState: useMemo(() => () => createGame(activeSeed), [activeSeed]),
    autoDraw: true,
    ...(controllers ? { controllers } : {}),
    ...(botDelayMs === undefined ? {} : { botDelayMs }),
  });

  const nameFor = names.solo(game.you);
  const waits = useMemo(() => computeWaits(game.model), [game.model]);

  const start = (value: string): void => {
    const next = value.trim().length > 0 ? value.trim() : newSeed();
    setActiveSeed(next);
    setSeedInput(next);
    game.reset(createGame(next));
    setGameSerial((value) => value + 1);
  };

  /*
   * The seed is a replay handle, not a thing to set up before playing. It used
   * to be a labelled text field and three buttons across the top of the screen,
   * which is the single control that did most to make this look like an admin
   * tool. It belongs with the other things you change between rounds.
   */
  const menu = (
    <>
      <section className="menu-section">
        <h3 className="sheet__heading">{t("solo.title")}</h3>
        <p className="muted menu-section__note">{t("solo.subtitle")}</p>
        <form
          className="seed-form"
          onSubmit={(event) => {
            event.preventDefault();
            start(seedInput);
          }}
        >
          <label htmlFor="seed">{t("solo.seed")}</label>
          <input
            id="seed"
            value={seedInput}
            onChange={(event) => setSeedInput(event.target.value)}
            aria-label={t("solo.seedAria")}
          />
          <div className="seed-form__actions">
            <button type="submit" className="button button--ghost">
              {t("solo.playSeed")}
            </button>
            <button type="button" className="button button--primary" onClick={() => start("")}>
              {t("solo.newGame")}
            </button>
            <button type="button" className="button button--ghost" onClick={() => start(activeSeed)}>
              {t("solo.replay")}
            </button>
          </div>
        </form>
        <p className="menu-section__meta muted">
          {t("solo.footer", { ruleSet: game.model.ruleSetId, seed: activeSeed })}
        </p>
      </section>

      <CoachPanel
        advice={game.advice}
        situation={describeSituation(game.state, game.you)}
        nameFor={nameFor}
        onAsk={game.showHint}
        {...(game.advice?.action
          ? { onFollow: () => game.submit(game.advice?.action as never) }
          : {})}
      />
      <EventLog entries={game.log} nameFor={nameFor} />
      <HelpPanel />
      <TableSettings />
    </>
  );

  return (
    <GameShell
      mode="solo"
      onMode={nav?.onMode ?? (() => {})}
      onExit={nav?.onExit ?? (() => {})}
      menu={menu}
      notice={
        game.notice ? (
          <p className="notice" role="alert">
            {tm(game.notice, { playerName: nameFor })}
            <button type="button" className="notice__close" onClick={game.clearNotice}>
              {t("notice.close")}
            </button>
          </p>
        ) : null
      }
      overlay={
        game.model.result ? (
          <EndPanel
            result={game.model.result}
            you={game.you}
            nameFor={nameFor}
            onNewGame={() => start("")}
            onRestart={() => start(activeSeed)}
            restartLabel={t("solo.replaySeed")}
          />
        ) : null
      }
    >
      <GameTable
        model={game.model}
        nameFor={nameFor}
        waits={waits}
        hintTileId={game.hintTileId}
        onSubmit={game.submit}
        manualDraw={false}
        onHint={game.showHint}
        suggested={game.advice?.action ?? null}
        ceremonyKey={`${activeSeed}:${gameSerial}`}
      />
    </GameShell>
  );
}
